import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Button,
  FieldError,
  Input,
  Label,
  Modal,
  TextArea,
  TextField,
} from "@heroui/react";
import { Segment } from "@heroui-pro/react";
import { isAxiosError } from "axios";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { REFUND_SCHEMA } from "./schema";

import { notify } from "@/components/kit";
import { aed, StatusChip } from "@/components/sales/shared";
import { useRefund } from "@/hooks/api/payment/useRefund";

interface OrderRefundItem {
  id: string;
  name: string;
  status: string;
  quantity: number;
  price: number;
}

interface OrderRefundModalProps {
  isOpen: boolean;
  orderId: string;
  displayId: string;
  totalAmount: number;
  items: OrderRefundItem[];
  onClose: () => void;
}

export const OrderRefundModal = ({
  isOpen,
  orderId,
  displayId,
  totalAmount,
  items,
  onClose,
}: OrderRefundModalProps) => {
  const { mutate: refund, isError, error, isPending } = useRefund();
  // What is typed in the amount box; the form holds the number it means.
  const [amountText, setAmountText] = useState(String(totalAmount));

  const {
    handleSubmit,
    formState: { errors },
    watch,
    control,
    setValue,
  } = useForm({
    resolver: zodResolver(REFUND_SCHEMA),
    defaultValues: {
      amount: totalAmount,
      reason: "",
    },
  });

  const currentAmount = watch("amount");
  const isFullRefund = currentAmount === totalAmount;

  const errorMessage = isError
    ? isAxiosError(error)
      ? error.response?.data.message
      : error.message
    : null;

  const setAmount = (text: string) => {
    const amount = parseFloat(text);

    setAmountText(text);
    setValue("amount", Number.isFinite(amount) ? amount : 0, {
      shouldValidate: true,
    });
  };

  const handleRefund = () => {
    const amount = Number(watch("amount"));
    const reason = watch("reason");

    refund(
      { orderId, data: { amount, reason } },
      {
        onSuccess: () => {
          notify.success(
            "Refund processed",
            `AED ${amount.toFixed(2)} has been refunded`,
          );
          onClose();
        },
        onError: (failure) => {
          console.error(failure);
          notify.error("Refund failed", "Please try again");
        },
      },
    );
  };

  return (
    <Modal.Backdrop
      isOpen={isOpen}
      variant="opaque"
      onOpenChange={(open) => !open && onClose()}
    >
      <Modal.Container placement="center" scroll="inside">
        <Modal.Dialog className="w-full max-w-lg">
          <form
            className="flex min-h-0 flex-1 flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              handleSubmit(handleRefund)();
            }}
          >
            <Modal.CloseTrigger />
            <Modal.Header>
              <Modal.Heading>Refund Order #{displayId}</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="flex flex-col gap-5 p-1">
              {isError ? (
                <Alert status="danger">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Title>Refund Failed</Alert.Title>
                    <Alert.Description>{errorMessage}</Alert.Description>
                  </Alert.Content>
                </Alert>
              ) : null}

              <section className="flex flex-col gap-1">
                <h3 className="text-muted text-xs font-medium">Order items</h3>
                <ul className="divide-separator divide-y">
                  {items.map((item, index) => (
                    <li
                      key={item.id ?? index}
                      className="flex items-center gap-3 py-2.5"
                    >
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-medium">
                          {item.name}
                        </span>
                        <span className="text-muted text-xs tabular-nums">
                          Qty {item.quantity} × {aed(item.price)}
                        </span>
                      </div>
                      <StatusChip status={item.status} />
                      <span className="w-24 text-right text-sm font-medium tabular-nums">
                        {aed(item.quantity * Number(item.price))}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Refund amount</span>
                  <Segment
                    aria-label="Refund type"
                    selectedKey={isFullRefund ? "full" : "partial"}
                    size="sm"
                    onSelectionChange={(key) =>
                      key === "full"
                        ? setAmount(String(totalAmount))
                        : setAmount("")
                    }
                  >
                    <Segment.Item id="full">Full Refund</Segment.Item>
                    <Segment.Item id="partial">
                      <Segment.Separator />
                      Partial
                    </Segment.Item>
                  </Segment>
                </div>
                <TextField
                  aria-label="Refund amount"
                  className="flex flex-col gap-1.5"
                  isInvalid={!!errors.amount}
                  value={amountText}
                  onChange={setAmount}
                >
                  <div className="relative">
                    <span className="text-muted pointer-events-none absolute inset-y-0 start-3 flex items-center text-sm">
                      AED
                    </span>
                    <Input
                      className="w-full ps-12 font-semibold tabular-nums"
                      inputMode="decimal"
                      max={totalAmount}
                      min={0}
                      step="any"
                      type="number"
                      variant="secondary"
                    />
                  </div>
                  <p className="text-muted text-xs">
                    Maximum refundable: {aed(totalAmount)}
                  </p>
                  <FieldError>{errors.amount?.message}</FieldError>
                </TextField>
              </section>

              <Controller
                control={control}
                name="reason"
                render={({ field }) => (
                  <TextField
                    className="flex flex-col gap-1.5"
                    isInvalid={!!errors.reason}
                    name={field.name}
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                  >
                    <Label>Reason for refund</Label>
                    <TextArea
                      placeholder="e.g. Customer requested cancellation, item out of stock"
                      rows={3}
                      variant="secondary"
                    />
                    <FieldError>{errors.reason?.message}</FieldError>
                  </TextField>
                )}
              />

              <dl className="border-separator flex flex-col gap-1.5 border-t pt-4 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted">Order total</dt>
                  <dd className="tabular-nums">{aed(totalAmount)}</dd>
                </div>
                <div className="flex justify-between font-semibold">
                  <dt>Refund amount</dt>
                  <dd className="tabular-nums">
                    {aed(Number(currentAmount) || 0)}
                  </dd>
                </div>
              </dl>
            </Modal.Body>
            <Modal.Footer>
              <Button
                isDisabled={isPending}
                type="button"
                variant="ghost"
                onPress={onClose}
              >
                Cancel
              </Button>
              <Button
                isDisabled={!currentAmount || currentAmount <= 0}
                isPending={isPending}
                type="submit"
              >
                Process Refund
              </Button>
            </Modal.Footer>
          </form>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
};
